import { useEffect } from 'react'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { Location } from 'app/generated/graphql'
import {
  AgentBasics,
  BookedType,
  DEFAULT_GREETING,
  HoursLine,
  formatOpenHours,
  renderGreeting,
  resolveAgentPhones,
  resolveBookedTypes,
} from 'app/lib/aiAgentOverview'
import type { PhraseRule } from 'app/lib/aiAgentPhraseRules'
import { useAIAgentStore } from 'app/stores/StoreContext'
import { bg } from 'lib/background'
import { useUser } from 'app/utils'

export type AiAgentOverview = {
  loading: boolean
  /** The read failed, or the location has no agent configuration yet. */
  unavailable: boolean
  status: { voiceLive: boolean; smsOn: boolean; testNumber?: string }
  agent: {
    agentName?: string
    greeting: string
    businessName?: string
    /** No business name is set, so the agent uses the location's name shown. */
    businessNameFromLocation: boolean
    businessAddress?: string
    mainPhone?: string
    transferPhone?: string
    hours: HoursLine[]
    /** Where the agent reads business hours from: the location's hours, or the practice schedule the PMS sends. */
    hoursSource: `location` | `schedule`
    /** The location's weekly hours as stored, for the hours editor. */
    openHours: Location[`openHours`]
    /** The voice agent's compiler tools (super users only), which carry the hours source. */
    voiceAgentTools: string[]
    postBookingMessage?: string
    /** What the agent says after booking a hygiene visit. */
    hygienePostBookingMessage?: string
    smsConfirmationEnabled: boolean
    /** Say This, Not That rules as stored. */
    phraseRules: PhraseRule[]
    /** The location's own name, which the confirmation text uses. */
    practiceName: string
    timezone?: string | null
  }
  /** The editable basics as stored, greeting as its template, for the edit form. */
  basics: AgentBasics
  /** Reads the agent and the location again after a save, without going back to loading. */
  reload: () => void
  books: BookedType[]
  /** Offers and services as stored, for the editor. */
  editable: { offers: EditableOffer[]; services: EditableService[] }
  /** How the practice's schedule reaches Trackable; the Availability section reads it. */
  pms: { externalSlots: boolean; standardMode: boolean }
}

const nonEmpty = (value: string | null | undefined): string | undefined => (value ? value : undefined)

/**
 * Everything the overview sections show for one location: the agent config
 * (public read, see graphql.ts), the location record (status, phones, hours) and
 * the appointment types the config's category ids point at.
 */
export function useAiAgentOverview(locationId: string | undefined): AiAgentOverview {
  const agentStore = useAIAgentStore()
  const isSuper = useUser()?.type === `super`

  useEffect(() => {
    if (!locationId) return
    bg(agentStore.loadOverview(locationId, isSuper))
    return () => agentStore.abortOverview()
  }, [locationId, isSuper, agentStore])

  const records = locationId ? agentStore.overviews.get(locationId) : undefined
  const config = locationId ? agentStore.configsByLocationId.get(locationId) : undefined
  const location = records?.location
  const appointmentTypes = records?.appointmentTypes ?? []
  const voiceAgentTools = records?.voiceAgentTools ?? []
  const phones = resolveAgentPhones(location)

  return {
    loading: !records,
    unavailable: !!records && !config,
    status: {
      voiceLive: !!location?.isAIAgentLive,
      smsOn: !!location?.products?.includes(`SMS Agent`),
      testNumber: formatPhoneNumber(location?.twilioNumbers?.find((n) => n.type === `ai-testing`)?.phone),
    },
    agent: {
      agentName: nonEmpty(config?.agentName),
      greeting: renderGreeting(
        nonEmpty(config?.greeting) ?? DEFAULT_GREETING,
        nonEmpty(config?.businessName) ?? location?.name,
        config?.agentName,
      ),
      businessName: nonEmpty(config?.businessName) ?? nonEmpty(location?.name),
      businessNameFromLocation: !nonEmpty(config?.businessName) && !!nonEmpty(location?.name),
      businessAddress: nonEmpty(config?.businessAddress),
      mainPhone: formatPhoneNumber(phones.main),
      transferPhone: formatPhoneNumber(phones.transfer),
      hours: formatOpenHours(location?.openHours),
      openHours: location?.openHours,
      voiceAgentTools,
      hoursSource: voiceAgentTools.includes(`business_hours_from_schedule`) ? `schedule` : `location`,
      postBookingMessage: nonEmpty(config?.postBookingMessage),
      hygienePostBookingMessage: nonEmpty(config?.hygienePostBookingMessage),
      // Unset means on: the API sends the text unless the flag is explicitly false.
      smsConfirmationEnabled: config?.smsConfirmationEnabled !== false,
      phraseRules: config?.sayThisNotThatRules ?? [],
      practiceName: location?.name ?? ``,
      timezone: location?.timezone,
    },
    basics: {
      agentName: config?.agentName ?? ``,
      businessName: nonEmpty(config?.businessName) ?? location?.name ?? ``,
      greeting: nonEmpty(config?.greeting) ?? DEFAULT_GREETING,
      businessAddress: config?.businessAddress ?? ``,
      officePhone: formatPhoneNumber(phones.main) ?? ``,
      transferPhone: formatPhoneNumber(phones.transfer) ?? ``,
    },
    reload: () => {
      if (locationId) bg(agentStore.loadOverview(locationId, isSuper))
    },
    books: resolveBookedTypes(config, appointmentTypes),
    editable: {
      offers: (config?.offers ?? []).map((offer) => ({
        name: offer.name ?? ``,
        patientStatus: offer.patientStatus ?? `New Patient`,
        eligibleInsurance: offer.eligibleInsurance ?? `Any`,
        onlyEmergencyEligible: !!offer.onlyEmergencyEligible,
      })),
      services: [
        ...(config?.services ?? []).map((service) => toEditableService(service, true)),
        ...(config?.servicesNotOffered ?? []).map((service) => toEditableService(service, false)),
      ],
    },
    pms: {
      externalSlots: !!location?.carestackId || !!location?.ascendLocationId,
      // Same test the API uses: standard mode only applies to Kolla-connected locations.
      standardMode: location?.availabilityMode === `standard` && !!location?.kollaConsumerId,
    },
  }
}

/** An offer as the editor holds it. */
export type EditableOffer = {
  name: string
  patientStatus: string
  eligibleInsurance: string
  onlyEmergencyEligible: boolean
}

/** A service as the editor holds it: offered and not-offered services share one list. */
export type EditableService = {
  name: string
  lowPrice: string
  highPrice: string
  consultRequired: boolean
  consultPrice: string
  offered: boolean
}

function toEditableService(
  service: {
    name?: string | null
    lowPrice?: number | null
    highPrice?: number | null
    consultRequired?: boolean | null
    consultPrice?: number | null
  },
  offered: boolean,
): EditableService {
  return {
    name: service.name ?? ``,
    lowPrice: service.lowPrice == null ? `` : String(service.lowPrice),
    highPrice: service.highPrice == null ? `` : String(service.highPrice),
    consultRequired: !!service.consultRequired,
    consultPrice: service.consultPrice == null ? `` : String(service.consultPrice),
    offered,
  }
}
